import React, { useEffect, useMemo, useState } from "react";
import DatePicker from "react-datepicker";
import {
  Container,
  Form,
  Button,
  Row,
  Col,
  Alert,
  Card,
  InputGroup,
  Spinner,
} from "react-bootstrap";
import "react-datepicker/dist/react-datepicker.css";
import { api } from "../api";

// Set to false to close bookings and show the "fully booked" message.
// Flip back to true to re-enable the enquiry form.
const BOOKINGS_OPEN = false;

const DETAILS_MAX = 500;

type EventType = "Wedding" | "Corporate" | "Birthday" | "Festival" | "Other";

function timeToMinutes(t: string): number {
  // expects "HH:MM"
  const [hh, mm] = t.split(":").map((n) => Number(n));
  if (!Number.isFinite(hh) || !Number.isFinite(mm)) return NaN;
  return hh * 60 + mm;
}

function buildLocalDateWithTime(pickedDate: Date, hhmm: string): Date {
  // Avoid timezone surprises from datepicker by rebuilding a clean local date:
  // new Date(year, monthIndex, day, hh, mm, ss, ms)
  const [hh, mm] = hhmm.split(":").map(Number);

  const y = pickedDate.getFullYear();
  const m = pickedDate.getMonth(); // 0-based
  const d = pickedDate.getDate();

  return new Date(y, m, d, hh ?? 0, mm ?? 0, 0, 0);
}

const BookEvent: React.FC = () => {
  const [date, setDate] = useState<Date | null>(null);
  const [startTime, setStartTime] = useState<string>("18:00");
  const [endTime, setEndTime] = useState<string>("21:00");
  const [eventType, setEventType] = useState<EventType>("Wedding");
  const [guests, setGuests] = useState<number>(80);

  // (Optional) if you later fetch disabled dates:
  const [unavailableDates] = useState<Date[]>([]);

  const [name, setName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [email, setEmail] = useState("");
  const [location, setLocation] = useState("");
  const [details, setDetails] = useState("");
  const [agree, setAgree] = useState(false);

  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Auto-dismiss alerts
  useEffect(() => {
    if (!successMessage && !errorMessage) return;
    const t = setTimeout(() => {
      setSuccessMessage("");
      setErrorMessage("");
    }, 5000);
    return () => clearTimeout(t);
  }, [successMessage, errorMessage]);

  // Bootstrap-looking DatePicker input
  const DateInput = React.forwardRef<HTMLInputElement, any>(
    ({ value, onClick, placeholder }, ref) => (
      <InputGroup className="w-100 flex-nowrap">
        <InputGroup.Text className="bg-secondary text-white border-secondary" aria-hidden>
          📅
        </InputGroup.Text>
        <Form.Control
          ref={ref}
          value={value || ""}
          placeholder={placeholder}
          readOnly
          onClick={onClick}
          className="w-100"
          style={{ minWidth: 0 }}
        />
      </InputGroup>
    )
  );
  DateInput.displayName = "DateInput";

  const detailsCount = useMemo(() => details.length, [details]);

  const minDate = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const timeError = useMemo(() => {
    if (!startTime || !endTime) return "";
    const s = timeToMinutes(startTime);
    const e = timeToMinutes(endTime);
    if (!Number.isFinite(s) || !Number.isFinite(e)) return "Invalid time format.";
    if (e <= s) return "End time must be after start time.";
    return "";
  }, [startTime, endTime]);

  const isFormValid =
    !!name &&
    !!email &&
    !!phoneNumber &&
    !!date &&
    !!startTime &&
    !!endTime &&
    !!location &&
    Number.isFinite(guests) &&
    guests > 0 &&
    !!eventType &&
    !!details &&
    agree &&
    !timeError;

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!isFormValid) {
      setErrorMessage(timeError || "Please complete all required fields and confirm consent.");
      setSuccessMessage("");
      return;
    }

    setIsSubmitting(true);

    try {
      const picked = date as Date;

      // ✅ Build local datetimes safely (no timezone traps)
      const startDt = buildLocalDateWithTime(picked, startTime);
      const endDt = buildLocalDateWithTime(picked, endTime);

      const start_date = Math.floor(startDt.getTime() / 1000);
      const end_date = Math.floor(endDt.getTime() / 1000);

      if (end_date <= start_date) {
        setErrorMessage("End time must be after start time.");
        setSuccessMessage("");
        return;
      }

      // Light client-side hardening (backend must validate too)
      const safeName = name.trim().slice(0, 120);
      const safePhone = phoneNumber.trim().slice(0, 30);
      const safeEmail = email.trim().slice(0, 254);
      const safeLocation = location.trim().slice(0, 255);
      const safeDetails = details.trim().slice(0, DETAILS_MAX);

      await api.post("/book-event/", {
        // ✅ new backend contract
        start_date,
        end_date,

        eventType,
        guests,

        name: safeName,
        phoneNumber: safePhone,
        email: safeEmail,

        // keep both keys (harmless) in case you still read one server-side
        address: safeLocation,
        location: safeLocation,

        details: safeDetails,
        agree,
      });

      setSuccessMessage("Your booking request has been submitted!");
      setErrorMessage("");

      // Reset
      setDate(null);
      setStartTime("18:00");
      setEndTime("21:00");
      setEventType("Wedding");
      setGuests(80);
      setName("");
      setPhoneNumber("");
      setEmail("");
      setLocation("");
      setDetails("");
      setAgree(false);
    } catch (err: any) {
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.detail ||
        "Failed to submit booking request.";
      setErrorMessage(msg);
      setSuccessMessage("");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!BOOKINGS_OPEN) {
    return (
      <section className="py-5 text-white">
        <Container>
          <Row className="mb-3">
            <Col>
              <h1 className="fw-bold">Book an Event</h1>
            </Col>
          </Row>

          <Card className="shadow-sm bg-custom-dark text-white border-secondary">
            <Card.Body className="text-center py-5">
              <div style={{ fontSize: "2.5rem" }} aria-hidden>
                🍕
              </div>
              <h2 className="fw-bold mt-3">We're Fully Booked</h2>
              <p className="lead text-white-50 mb-2">
                Thank you so much for your interest in Bartollini Pizza!
              </p>
              <p className="text-white-50 mb-0" style={{ maxWidth: 620, margin: "0 auto" }}>
                We're sincerely sorry, but we are fully booked at the moment and are
                not able to accept new event enquiries for now. Please check back soon —
                we'd love to cater your event once availability opens back up.
              </p>
              <p className="text-white-50 mt-3 mb-0">
                In the meantime, feel free to reach out via our{" "}
                <a href="/contact" className="text-decoration-underline text-white">
                  Contact page
                </a>{" "}
                with any questions.
              </p>
            </Card.Body>
          </Card>
        </Container>
      </section>
    );
  }

  return (
    <section className="py-5 text-white">
      <Container>
        <Row className="mb-3">
          <Col>
            <h1 className="fw-bold">Book an Event</h1>
          </Col>
        </Row>

        {successMessage && (
          <Alert variant="success" className="mb-4">
            {successMessage}
          </Alert>
        )}
        {errorMessage && (
          <Alert variant="danger" className="mb-4">
            {errorMessage}
          </Alert>
        )}

        <Form onSubmit={handleSubmit}>
          <Card className="shadow-sm bg-custom-dark text-white border-secondary">
            <Card.Body>
              {/* Row 1: Name · Email · Phone */}
              <Row className="g-3">
                <Col md={4}>
                  <Form.Group controlId="formName">
                    <Form.Label>Name</Form.Label>
                    <InputGroup>
                      <InputGroup.Text className="bg-secondary text-white border-secondary" aria-hidden>
                        👤
                      </InputGroup.Text>
                      <Form.Control
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Full name"
                        maxLength={120}
                        required
                      />
                    </InputGroup>
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group controlId="formEmail">
                    <Form.Label>Email</Form.Label>
                    <InputGroup>
                      <InputGroup.Text className="bg-secondary text-white border-secondary" aria-hidden>
                        ✉️
                      </InputGroup.Text>
                      <Form.Control
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@example.com"
                        maxLength={254}
                        required
                      />
                    </InputGroup>
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group controlId="formPhoneNumber">
                    <Form.Label>Phone</Form.Label>
                    <InputGroup>
                      <InputGroup.Text className="bg-secondary text-white border-secondary" aria-hidden>
                        📞
                      </InputGroup.Text>
                      <Form.Control
                        type="tel"
                        inputMode="tel"
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, ""))}
                        placeholder="e.g. 0892063068"
                        maxLength={30}
                        required
                      />
                    </InputGroup>
                  </Form.Group>
                </Col>
              </Row>

              {/* Row 2: Event Date · Start Time · End Time */}
              <Row className="g-3 mt-1">
                <Col md={4}>
                  <Form.Group controlId="formDate">
                    <Form.Label className="d-block">Event Date</Form.Label>
                    <div className="mt-1">
                      <DatePicker
                        selected={date}
                        onChange={(d) => setDate(d)}
                        minDate={minDate}
                        dateFormat="dd/MM/yyyy"
                        placeholderText="Select a date"
                        customInput={<DateInput />}
                        excludeDates={unavailableDates}
                        required
                        wrapperClassName="w-100"
                      />
                    </div>
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group controlId="formStartTime">
                    <Form.Label>Start Time</Form.Label>
                    <InputGroup>
                      <InputGroup.Text className="bg-secondary text-white border-secondary" aria-hidden>
                        ⏰
                      </InputGroup.Text>
                      <Form.Control
                        type="time"
                        value={startTime}
                        onChange={(e) => setStartTime(e.target.value)}
                        required
                      />
                    </InputGroup>
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group controlId="formEndTime">
                    <Form.Label>End Time (Estimate)</Form.Label>
                    <InputGroup>
                      <InputGroup.Text className="bg-secondary text-white border-secondary" aria-hidden>
                        🕘
                      </InputGroup.Text>
                      <Form.Control
                        type="time"
                        value={endTime}
                        onChange={(e) => setEndTime(e.target.value)}
                        required
                        isInvalid={!!timeError}
                      />
                      <Form.Control.Feedback type="invalid">
                        {timeError}
                      </Form.Control.Feedback>
                    </InputGroup>
                  </Form.Group>
                </Col>
              </Row>

              {/* Row 3: Location · Event Type · Guests */}
              <Row className="g-3 mt-1">
                <Col md={4}>
                  <Form.Group controlId="formLocation">
                    <Form.Label>Location</Form.Label>
                    <InputGroup>
                      <InputGroup.Text className="bg-secondary text-white border-secondary" aria-hidden>
                        📍
                      </InputGroup.Text>
                      <Form.Control
                        type="text"
                        value={location}
                        onChange={(e) => setLocation(e.target.value)}
                        placeholder="Venue name / Eircode"
                        maxLength={255}
                        required
                      />
                    </InputGroup>
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group controlId="formEventType">
                    <Form.Label>Event Type</Form.Label>
                    <InputGroup>
                      <InputGroup.Text className="bg-secondary text-white border-secondary" aria-hidden>
                        🎉
                      </InputGroup.Text>
                      <Form.Select
                        className="border-secondary"
                        value={eventType}
                        onChange={(e) => setEventType(e.target.value as EventType)}
                      >
                        {(["Wedding", "Corporate", "Birthday", "Festival", "Other"] as EventType[]).map((opt) => (
                          <option key={opt} value={opt}>
                            {opt}
                          </option>
                        ))}
                      </Form.Select>
                    </InputGroup>
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group controlId="formGuests">
                    <Form.Label>Guests (Estimate)</Form.Label>
                    <InputGroup>
                      <InputGroup.Text className="bg-secondary text-white border-secondary" aria-hidden>
                        👥
                      </InputGroup.Text>
                      <Form.Control
                        type="number"
                        min={1}
                        step={1}
                        value={guests}
                        onChange={(e) => setGuests(Number(e.target.value))}
                        placeholder="Approximate headcount"
                        required
                      />
                    </InputGroup>
                  </Form.Group>
                </Col>
              </Row>

              {/* Row 4: Additional Notes */}
              <Form.Group controlId="formDetails" className="mt-3">
                <Form.Label>Additional Notes</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={5}
                  value={details}
                  maxLength={DETAILS_MAX}
                  onChange={(e) => setDetails(e.target.value)}
                  placeholder="E.g. venue access, dietary needs, service time..."
                  required
                />
                <div className="d-flex justify-content-end">
                  <small className="text-white-50">
                    {detailsCount}/{DETAILS_MAX}
                  </small>
                </div>
              </Form.Group>

              {/* Row 5: Consent + Submit */}
              <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between mt-3 gap-3">
                <Form.Check
                  id="formAgree"
                  type="checkbox"
                  checked={agree}
                  onChange={(e) => setAgree(e.target.checked)}
                  label={
                    <>
                      I agree to the{" "}
                      <a
                        href="/privacy-policy"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-decoration-underline text-white"
                      >
                        Privacy Policy
                      </a>{" "}
                      and consent to Bartollini Pizza storing and processing my personal data
                      in order to respond to this enquiry.
                    </>
                  }
                />
                <div className="d-flex gap-2">
                  <Button variant="outline-success" type="submit" disabled={isSubmitting}>
                    {isSubmitting ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                          className="me-2"
                        />
                        Submitting…
                      </>
                    ) : (
                      "Submit Enquiry"
                    )}
                  </Button>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Form>
      </Container>
    </section>
  );
};

export default BookEvent;
